// AIS / ISS computation helper.
// ISS = sum of squares of the 3 highest AIS severities across 6 body regions.
// If any AIS severity == 6 (Maximal/Unsurvivable), ISS is automatically 75.
// Valid AIS values are 1..6. Severities <= 0 or null are ignored.

export const AIS_REGIONS = [
  "Head/Neck",
  "Face",
  "Chest",
  "Abdomen",
  "Extremity",
  "External",
];

// Map various AI-returned region strings to canonical names.
export function normalizeRegion(region) {
  if (!region) return null;
  const r = String(region).toLowerCase();
  if (r.includes("head") || r.includes("neck") || r.includes("brain") || r.includes("spine")) {
    return "Head/Neck";
  }
  if (r.includes("face") || r.includes("facial") || r.includes("eye") || r.includes("ear")) {
    return "Face";
  }
  if (r.includes("chest") || r.includes("thorax") || r.includes("thoracic") || r.includes("lung") || r.includes("rib")) {
    return "Chest";
  }
  if (r.includes("abdomen") || r.includes("abdominal") || r.includes("pelvis") || r.includes("pelvic")) {
    return "Abdomen";
  }
  if (
    r.includes("extremity") ||
    r.includes("extremit") ||
    r.includes("limb") ||
    r.includes("arm") ||
    r.includes("leg") ||
    r.includes("hip") ||
    r.includes("femur") ||
    r.includes("tibia") ||
    r.includes("humerus") ||
    r.includes("shoulder") ||
    r.includes("knee") ||
    r.includes("ankle") ||
    r.includes("wrist") ||
    r.includes("hand") ||
    r.includes("foot")
  ) {
    return "Extremity";
  }
  if (r.includes("external") || r.includes("skin") || r.includes("burn") || r.includes("laceration")) {
    return "External";
  }
  return null;
}

export function computeIss(injuries) {
  // Returns { iss: number, unsurvivable: boolean, regions: {region: maxAis}, top3: [{region, ais}] }
  const regionMax = {};
  AIS_REGIONS.forEach((r) => (regionMax[r] = 0));

  let unsurvivable = false;
  (injuries || []).forEach((inj) => {
    const ais = Number(inj.ais_severity);
    if (!ais || ais < 1 || ais > 6) return;
    if (ais === 6) unsurvivable = true;
    const region = normalizeRegion(inj.ais_region);
    if (!region) return;
    if (ais > regionMax[region]) regionMax[region] = ais;
  });

  if (unsurvivable) {
    const top3 = Object.entries(regionMax)
      .filter(([, v]) => v > 0)
      .map(([region, ais]) => ({ region, ais }))
      .sort((a, b) => b.ais - a.ais)
      .slice(0, 3);
    return { iss: 75, unsurvivable: true, regions: regionMax, top3 };
  }

  const top3 = Object.entries(regionMax)
    .filter(([, v]) => v > 0)
    .map(([region, ais]) => ({ region, ais }))
    .sort((a, b) => b.ais - a.ais)
    .slice(0, 3);

  const iss = top3.reduce((acc, x) => acc + x.ais * x.ais, 0);
  return { iss, unsurvivable: false, regions: regionMax, top3 };
}

// ISS severity bucket
export function issBucket(iss) {
  if (iss === 0) return { label: "N/A", tone: "muted" };
  if (iss < 9) return { label: "Minor", tone: "success" };
  if (iss < 16) return { label: "Moderate", tone: "warning" };
  if (iss < 25) return { label: "Serious", tone: "warning" };
  if (iss < 75) return { label: "Severe", tone: "danger" };
  return { label: "Critical", tone: "danger" };
}

export const AIS_SEVERITY_LABEL = {
  1: "Minor",
  2: "Moderate",
  3: "Serious",
  4: "Severe",
  5: "Critical",
  6: "Max",
};
